import dayjs from 'dayjs';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useEffect, useRef, useState } from 'react';
import { OrderInstruction } from '../../../components/maps/BundleMap';
import { DirectionsData } from '../../../fetch/mapboxApi';
import { CourierJson } from '../../../types/bundleTypes';
import { Region } from '../../../types/orderTypes';
import { onlyUnique } from '../../../utils/array';
import { combineInstructions, isSameLocation } from '../../../utils/maps';
import { createGroupColors } from '../../oldPlanner/planner-map/map/PlannerMapMapUtils';
import { useMapboxMap } from '../map/utils/useMapboxMap';
import './BundleMapboxMap.scss';
import {
  addCourierMarker,
  addInstructionsMarkers,
  addWaypointLayer,
  cleanupLayersAndSources,
} from './bundleMapUtils';

export const ORDER_INSTRUCTIONS_SOURCE = 'ORDER_INSTRUCTIONS_SOURCE';
export const ORDER_INSTRUCTIONS_LAYER = 'ORDER_INSTRUCTIONS_LAYER';
export const ORDER_INSTRUCTIONS_LABEL_LAYER = 'ORDER_INSTRUCTIONS_LABEL_LAYER';
export const COURIER_SOURCE = 'COURIER_SOURCE';
export const COURIER_SOURCE_CIRCLE_LAYER = 'COURIER_SOURCE_CIRCLE_LAYER';
export const COURIER_SOURCE_LABEL_LAYER = 'COURIER_SOURCE_LABEL_LAYER';
export const WAYPOINT_SOURCE = 'WAYPOINT_SOURCE';
export const WAYPOINT_LINE_LAYER = 'WAYPOINT_LINE_LAYER';

interface BundleMapboxMapProps {
  orderInstructions: OrderInstruction[];
  height?: number | string;
  width?: number | string;
  courier?: CourierJson;
  directions: DirectionsData;
  region: Region;
}
export const BundleMapboxMap = React.memo(
  ({
    orderInstructions,
    height,
    width,
    courier,
    directions,
    region,
  }: BundleMapboxMapProps) => {
    const map = useRef<mapboxgl.Map>(null);
    const mapContainer = useRef();
    const popupNode = new mapboxgl.Popup({ offset: 15 });
    const popup = useRef(popupNode);
    const [mapPointsLoaded, setMapPointsLoaded] = useState<boolean>(false);
    const [mapLoaded, setMapLoaded] = useState<boolean>(false);
    // Using last route coordinate as map initial position
    const { length, [length - 1]: lastLatLng } = directions.routeCoordinates;

    useMapboxMap(
      map,
      mapContainer,
      region,
      { fitToAreaOverlayBounds: false },
      lastLatLng
    );

    const deliveryInstructions: OrderInstruction[] = orderInstructions
      .filter(instruction => instruction.instruction.type === 'DELIVERY')
      .reduce((prev: OrderInstruction[], next) => {
        /* Add the ListNumber of orders */
        next.listNumber = next.label;

        if (!next.order.idWithListNumber) {
          next.order.idWithListNumber = next.order.id;
        }

        // Find instructions with repeated coordinates
        const repeatedIndex = prev.findIndex(value =>
          isSameLocation(value, next)
        );

        // If repeated coordinates join them with an M label
        // and combine order IDs separated with commas
        if (repeatedIndex !== -1) {
          prev[repeatedIndex] = combineInstructions(prev[repeatedIndex], next);
          return prev;
        }

        /* Only add the listNumber when it's not added yet */
        if (!next.order.idWithListNumber?.match(/\(.*?\)/)) {
          next.order.idWithListNumber = `${next.order.id} (${next.listNumber})`;
        }

        return [...prev, next];
      }, []);

    const windows = Array.from(
      createGroupColors(
        deliveryInstructions
          .map(
            instruction =>
              `${dayjs(instruction.order.deliverFrom).format(
                'HH:mm'
              )} - ${dayjs(instruction.order.deliverUntil).format('HH:mm')}`
          )
          .filter(onlyUnique)
      )
    );

    useEffect(() => {
      map.current.on('load', () => {
        setMapLoaded(true);
      });
    }, []);

    useEffect(() => {
      if (mapLoaded) {
        map.current.resize();
      }
      // we "listen" to width and hight
      // if any of those change, we run
      // resize()
    }, [width, height, mapLoaded]);

    useEffect(() => {
      const mapCurrent = map.current;

      if (mapLoaded) {
        cleanupLayersAndSources(map.current);
        /* Waypoint layer needs the coordinate with 2 or more positions available in the array */
        directions.routeCoordinates.length > 1 &&
          addWaypointLayer(map.current, directions);
        addInstructionsMarkers(
          map.current,
          deliveryInstructions,
          windows,
          popup.current,
          !mapPointsLoaded
        );
        courier && addCourierMarker(map.current, courier, popup.current);
        setMapPointsLoaded(true);
      }

      return () => {
        cleanupLayersAndSources(mapCurrent);
      };
    }, [
      courier,
      deliveryInstructions,
      directions,
      directions.routeCoordinates,
      mapLoaded,
      mapPointsLoaded,
      windows,
    ]);

    return (
      <div
        className="bundle-mapbox-map__map"
        style={{
          width: width || '100%',
          height: height || 500,
          position: 'relative',
        }}
      >
        <div className="bundle-mapbox-map__map__legend">
          <h6>Click to show info</h6>
          <div>
            {windows.map(window => (
              <div
                key={window[0]}
                className="bundle-mapbox-map__map__legend__windows"
              >
                <div
                  style={{ width: 16, height: 16, backgroundColor: window[1] }}
                />
                {window[0]}
              </div>
            ))}
            <div className="bundle-mapbox-map__map__legend__windows">
              <div
                style={{ width: 16, height: 16, backgroundColor: '#661aff' }}
              />
              Multiple windows
            </div>
            {deliveryInstructions.some(
              instruction =>
                instruction.order.deviations?.length > 0 ||
                instruction.order.courierDeviations
            ) && (
              <div className="bundle-mapbox-map__map__legend__windows">
                <div
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: 'yellow',
                  }}
                />
                Deviated
              </div>
            )}
          </div>
        </div>
        <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
      </div>
    );
  }
);
